import { memo, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";

import { logAnalyticsEvent } from "@/constants/amplitude";
import { classByType } from "@/constants/cardStyleByIndex";
import { COMPONENTS } from "@/constants/components";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { ExperimentIds, ExperimentVariants } from "@/constants/experiments";
import { FitnessGoals } from "@/constants/fitnessGoal";
import { COMPONENTS_BY_QUESTION_TYPE } from "@/constants/questionComponents";
import { questionGroups } from "@/constants/questionGroups";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { getQuestions as getFemaleCalisthenicsQuestions } from "@/female/constants/calisthenics-questions";
import { getQuestions as getFemaleCalisthenicsWeightPredQuestions } from "@/female/constants/calisthenics-weight-predictor-questions";
import { getQuestions as getFemaleNewSequenceQs } from "@/female/constants/new-sequence-questions";
import { getQuestions as getFemaleQuestions } from "@/female/constants/questions";
import { getAllPossibleQuestions as getAllPossibleFemaleQuestions } from "@/female/utils/flatQuestions";
import { useDeviceType } from "@/hooks/useDeviceType";
import { usePreloadTargetAreasImages } from "@/hooks/usePreloadTargetAreasImages";
import { useWeightPredictor } from "@/hooks/useWeightPredictor";
import { getQuestions as getMaleCalisthenicsQuestions } from "@/male/constants/calisthenics-questions";
import { getQuestions as getMaleNewSequenceQs } from "@/male/constants/new-sequence-questions";
import { getQuestions as getMaleQuestions } from "@/male/constants/questions";
import { getAllPossibleQuestions } from "@/male/utils/flatQuestions";
import { getQuestions as getSeniorsQuestions } from "@/seniors/constants/questions";
import { Question } from "@/types/question";
import { Answers } from "@/utils/AnswersProvider";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";
import { useFlowManager } from "@/utils/providers/FlowProvider";
import { useStrapiQuestions } from "@/utils/providers/StrapiQuestionsProvider";
import { useFeatureValue } from "@growthbook/growthbook-react";

import Header from "../common/Header";
import useAnswers from "../utils/useAnswers";

export const CURRENT_SCREEN_INDEX = 1;

const contentOffsetStyles = `top-[92px] md:top-[132px]`;
const contentHeightStyles = `h-[calc(100%-92px)] md:h-[calc(100%-132px)]`;

const Main = () => {
    const { isAndroid } = useDeviceType();
    const { isFemale, prefix } = useGender();
    const [currIndex, setCurrIndex] = useState(0);
    const [prevIndex, setPrevIndex] = useState(0);
    const { answers, setAnswers, isSkipDateChart, getAnswersValue } = useAnswers();
    const [currentQuestionId, setCurrentQuestionId] = useState<QUESTIONS_IDS | undefined>();

    const { questions: strapiQuestions } = useStrapiQuestions({ questionId: currentQuestionId });

    const { isWeightPredictor } = useWeightPredictor();

    useFeatureValue("webtest_growth_book_first_AA", ExperimentVariants.NOT_ALLOCATED);

    const { isTest: isAIUserSummaryIter2Exp } = useExperiment({
        experimentId: ExperimentIds.AI_USER_SUMMARY_ITER2,
    });

    const nextComponent = useMemo(() => {
        if (isAIUserSummaryIter2Exp) {
            return COMPONENTS.NAME;
        }
        return !isFemale || !isSkipDateChart ? COMPONENTS.PROGRESS_LOADER : COMPONENTS.LOADER;
    }, [isFemale, isSkipDateChart, isAIUserSummaryIter2Exp]);

    const { onContinue, isFlowType } = useFlowManager({
        nextComponent,
        componentsToPreload: [COMPONENTS.PROGRESS_LOADER, COMPONENTS.LOADER],
    });

    const { isTest: isNumberOfStepsExp } = useExperiment({
        experimentId: ExperimentIds.NUMBER_OF_STEPS,
    });

    const { isTest: isSeniorsFunnelExp, variant: seniorsFunnelExpVariant } = useExperiment({
        experimentId: ExperimentIds.SENIORS_FUNNEL,
    });

    const { isTest: isNewQsSequenceExp } = useExperiment({
        experimentId: ExperimentIds.NEW_QS_SEQUENCE,
    });

    const questions = useMemo(() => {
        // TODO: will remove type casting after move all question ids to enum values
        if (isSeniorsFunnelExp) {
            return getSeniorsQuestions(prefix, answers, isFemale) as unknown as Question[];
        }

        if (isFlowType.calisthenics) {
            if (isFemale) {
                const getCalisthenicsFemaleQuestions = isWeightPredictor
                    ? getFemaleCalisthenicsWeightPredQuestions
                    : getFemaleCalisthenicsQuestions;

                return getCalisthenicsFemaleQuestions(prefix, answers) as unknown as Question[];
            } else {
                return getMaleCalisthenicsQuestions(prefix, answers) as unknown as Question[];
            }
        }

        if (isNewQsSequenceExp) {
            const getQuestionsFn = isFemale ? getFemaleNewSequenceQs : getMaleNewSequenceQs;
            return getQuestionsFn(
                prefix,
                answers,
                strapiQuestions,
                isAndroid,
                isWeightPredictor,
                isNumberOfStepsExp
            ) as unknown as Question[];
        }

        if (isFemale) {
            return getFemaleQuestions(
                prefix,
                answers,
                strapiQuestions,
                isAndroid,
                isWeightPredictor,
                isNumberOfStepsExp,
                isAIUserSummaryIter2Exp
            ) as unknown as Question[];
        } else {
            return getMaleQuestions(
                prefix,
                answers,
                strapiQuestions,
                isAndroid,
                isNumberOfStepsExp,
                isAIUserSummaryIter2Exp
            ) as unknown as Question[];
        }
    }, [
        isSeniorsFunnelExp,
        isFlowType.calisthenics,
        isNewQsSequenceExp,
        isFemale,
        prefix,
        answers,
        isWeightPredictor,
        strapiQuestions,
        isAndroid,
        isNumberOfStepsExp,
        isAIUserSummaryIter2Exp,
    ]);

    const [targetQuestions, setTargetQuestions] = useState<Question[]>(questions);
    const [allPossibleQuestions, setAllPossibleQuestions] = useState<Question[]>([]);

    useEffect(() => {
        const questions = isFemale
            ? getAllPossibleFemaleQuestions(prefix, isAndroid, {
                  isWeightPredictor,
                  isNumberOfStepsExp,
              })
            : getAllPossibleQuestions(prefix, isAndroid, { isNumberOfStepsExp });

        setAllPossibleQuestions(questions);
    }, [isFemale, targetQuestions, isWeightPredictor, isNumberOfStepsExp, prefix, isAndroid]);

    usePreloadTargetAreasImages(currentQuestionId);

    const handleAnswer = (ans: Answers) => {
        setAnswers((prev) => ({ ...prev, ...ans }));
    };

    const handleNext = (ans: Answers = {}) => {
        handleAnswer(ans);
        const isStrapiEndQuestions = !!strapiQuestions?.length;
        if (
            currIndex === questions.length - 1 ||
            !elements[elements.length - 1] ||
            (isFemale &&
                ans?.event_motivation &&
                ans.event_motivation === "none" &&
                !isStrapiEndQuestions) ||
            (isFemale && ans?.event_date === "skip" && !strapiQuestions?.length)
        ) {
            onContinue();
            return;
        }
        setTimeout(() => {
            setCurrIndex((prev) => {
                setPrevIndex(prev);
                return prev + 1;
            });
            window.scrollTo(0, 0);
        }, 200);
    };

    const elements = useMemo(() => {
        let temporaryQuestions = [...questions];
        if (getAnswersValue(QUESTIONS_IDS.EVENT_MOTIVATION) === "none") {
            temporaryQuestions = [
                ...temporaryQuestions.filter(({ id }) => id !== QUESTIONS_IDS.EVENT_DATE),
            ];
        }
        const location = getAnswersValue(QUESTIONS_IDS.LOCATION);
        if (location) {
            temporaryQuestions = [
                ...temporaryQuestions.filter((item) => {
                    if (location === "home") {
                        return item.id !== QUESTIONS_IDS.GYM_TYPE;
                    }
                    if (location === "gym") {
                        return item.id !== "equipment_by_environment";
                    }
                    return true;
                }),
            ];
        }
        if (
            getAnswersValue(QUESTIONS_IDS.WEB_CYCLE) === "no" ||
            getAnswersValue(QUESTIONS_IDS.WEB_CYCLE) === "dont_have"
        ) {
            temporaryQuestions = [
                ...temporaryQuestions.filter(
                    (item) =>
                        ![
                            QUESTIONS_IDS.PROMO_SCREEN_WORKOUT_SMARTLY,
                            QUESTIONS_IDS.WEB_CYCLE_DATE,
                            QUESTIONS_IDS.WEB_PERIOD_DAYS,
                            QUESTIONS_IDS.WEB_CYCLE_DAYS,
                            QUESTIONS_IDS.WEB_CYCLE_SYMPTOMS,
                            QUESTIONS_IDS.WEB_CYCLE_PHASE_CHART,
                        ].includes(item.id)
                ),
            ];
        }
        if (getAnswersValue(QUESTIONS_IDS.PRIMARY_GOAL) === FitnessGoals.GainMuscle) {
            temporaryQuestions = [
                ...temporaryQuestions.filter((item) => !["promo_change_face"].includes(item.id)),
            ];
        }
        if (getAnswersValue(QUESTIONS_IDS.WEB_REPRODUCTIVE_STAGE) === "menstrual") {
            temporaryQuestions = [
                ...temporaryQuestions.filter(
                    (item) => ![QUESTIONS_IDS.PROMO_SCREEN_REPRODUCTIVE_STAGE].includes(item.id)
                ),
            ];
        } else if (getAnswersValue(QUESTIONS_IDS.WEB_REPRODUCTIVE_STAGE)) {
            temporaryQuestions = [
                ...temporaryQuestions.filter(
                    (item) =>
                        ![
                            QUESTIONS_IDS.WEB_CYCLE,
                            QUESTIONS_IDS.PROMO_SCREEN_WORKOUT_SMARTLY,
                            QUESTIONS_IDS.WEB_CYCLE_DATE,
                            QUESTIONS_IDS.WEB_PERIOD_DAYS,
                            QUESTIONS_IDS.WEB_CYCLE_DAYS,
                            QUESTIONS_IDS.WEB_CYCLE_SYMPTOMS,
                            QUESTIONS_IDS.WEB_CYCLE_PHASE_CHART,
                        ].includes(item.id)
                ),
            ];
        }

        setTargetQuestions(temporaryQuestions);
        setCurrentQuestionId(temporaryQuestions[currIndex].id);
        return [
            ...(currIndex === 0 ? [null] : temporaryQuestions.slice(currIndex - 1, currIndex)),
            temporaryQuestions[currIndex],
            ...(currIndex === temporaryQuestions.length - 1
                ? [null]
                : temporaryQuestions.slice(currIndex + 1, currIndex + 2)),
        ];
    }, [currIndex, questions, getAnswersValue]);

    useEffect(() => {
        logAnalyticsEvent(WebEventTypes.VIEW_EVENT, {
            [WebEventProps.SCREEN]: `${elements[CURRENT_SCREEN_INDEX]?.event}`,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currIndex]);

    const handleBack = useCallback(
        (screen: string | undefined) => () => {
            logAnalyticsEvent(WebEventTypes.WEB_BUTTON_CLICK, {
                button_name: "back",
                screen: screen,
            });

            setCurrIndex((currIndex) => {
                setPrevIndex(currIndex);
                return currIndex - 1;
            });
        },
        []
    );

    const { event, withoutHeader } = elements[CURRENT_SCREEN_INDEX] ?? {};

    const showContent = isFlowType.seniors ? seniorsFunnelExpVariant : true;

    return (
        showContent && (
            <div className="overflow-hidden">
                <div className="zing-dynamic-screen zing-max-w flex w-screen flex-col">
                    <div className="relative grow">
                        {!withoutHeader && (
                            <Header
                                targetQuestions={targetQuestions}
                                allPossibleQuestions={allPossibleQuestions}
                                step={currIndex}
                                questionId={targetQuestions[currIndex].id}
                                prevTitle={targetQuestions[prevIndex]?.groupTitle as questionGroups}
                                title={targetQuestions[currIndex].groupTitle as questionGroups}
                                onBack={handleBack(event)}
                            />
                        )}
                        {elements.map((q, i) => {
                            if (!q) return null;
                            if (q.id === QUESTIONS_IDS.EVENT_DATE) {
                                q.eventName = getAnswersValue(QUESTIONS_IDS.EVENT_MOTIVATION);
                            }
                            if (q.id === QUESTIONS_IDS.PROMO_HELP) {
                                const plansFailed = getAnswersValue(QUESTIONS_IDS.PLAN_FAILED);
                                q.isPlansFailed = plansFailed === "yes";
                            }
                            const QuestionComponent = COMPONENTS_BY_QUESTION_TYPE[q?.type];
                            return (
                                <div
                                    key={q?.id + q?.title}
                                    className={classNames(
                                        `absolute w-screen max-w-full ${classByType[i]} transition-left overflow-y-scroll px-6 duration-300`,
                                        { [contentOffsetStyles]: !withoutHeader },
                                        { [contentHeightStyles]: !withoutHeader },
                                        { "h-full": withoutHeader }
                                    )}
                                >
                                    <div className="h-full w-full">
                                        <QuestionComponent
                                            question={q}
                                            index={i}
                                            isActive={targetQuestions[currIndex].id === q?.id}
                                            handleNext={handleNext}
                                            handleBack={handleBack(event)}
                                            className="top-[92px]"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        )
    );
};

export default memo(Main);
